import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { find, isEmpty, map, reduce } from 'lodash';

import { StringTranslation } from '@pimm/services/lib/sms-tenants/types';
import { ModuleScreen } from '@app/features/app';
import { WidgetStore } from '@app/features/calendar/store/widget.store';

export interface WidgetModule {
  identifier: string;
  isFavorite?: boolean;
  favoriteSeq?: number;
  component?: React.ComponentType;
  title?: string;
  titleTranslations?: StringTranslation[];
  sectionSeq?: number;
  sectionTitle?: string;
  sectionTitleTranslations?: StringTranslation[];
  seq?: number;
  payload?: Record<string, any>;
}

export interface WidgetModulesOptions {
  modules?: ModuleScreen[];
  permissions?: Record<string, unknown>;
  siteId?: string;
  userId?: string;
}

export interface WidgetModulesContextValue {
  widgets: WidgetModule[];
  updateWidgets: (_widgets: WidgetModule[]) => void;
}

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const WidgetModulesContext = createContext<WidgetModulesContextValue>(undefined!);

export type WidgetModulesProviderProps = React.PropsWithChildren<{
  registeredWidgets?: WidgetModule[];
  options?: WidgetModulesOptions;
}>;

export const WidgetModulesProvider = ({ children, registeredWidgets = [], options }: WidgetModulesProviderProps) => {
  const [widgets, setWidgets] = useState<WidgetModule[]>([]);
  const key = `${options?.siteId}:${options?.userId}`;

  const handleUpdate = useCallback((_widgets: WidgetModule[]) => {    // Ensure favorite sequence is kept in sync with the current order
    if (options?.siteId && options?.userId) {
      // Fire-and-forget; avoid surfacing cancellation/connection errors to the UI
      WidgetStore.set(options.siteId, options.userId, _widgets.map((_, index) => {
        return { identifier: _.identifier, seq: index };
      }),
      ).catch(() => {
        // Intentionally ignore storage errors here
      });
    }
    setWidgets((prevWidgets) =>
      prevWidgets.map((widget) => {
        const newIndex = _widgets.findIndex((w) => w?.identifier === widget.identifier);
        const isFavorite = newIndex !== -1;
        return { ...widget, isFavorite, favoriteSeq: isFavorite ? newIndex : undefined };
      })
    );
  }, [key]); // prettier-ignore

  useEffect(() => {
    const { siteId, userId } = options ?? {};

    if (!siteId || !userId) return;

    const populateWidgets = async () => {
      const favorites = await WidgetStore.get(siteId, userId);
      const filteredModules = options?.modules?.filter(_ => !!_.identifierId && !options.permissions?.[`appModule${_.name}PinRestricted`]);
      const mapRegisteredWidgets = reduce(filteredModules, (_widgets: WidgetModule[], { identifierId, ..._ }) => {
        const widgetModule = registeredWidgets.find(_ => _.identifier === identifierId);

        if (!widgetModule?.component) return _widgets;

        const favorite = favorites.find(_ => identifierId && _?.identifier === identifierId);
        const widget: WidgetModule = {
          identifier: identifierId!,
          title: _.title,
          titleTranslations: _.titleTranslations,
          sectionSeq: _.payload?.sectionSeq,
          sectionTitle: _.payload?.sectionTitle,
          sectionTitleTranslations: _.payload?.sectionTitleTranslations,
          isFavorite: !!favorite,
          favoriteSeq: favorite?.seq,
          payload: widgetModule?.payload,
          component: widgetModule?.component,
        };
        return [..._widgets, widget];
      }, []); // prettier-ignore

      setWidgets(mapRegisteredWidgets);
    };

    populateWidgets();
  }, [key, options?.modules, registeredWidgets, options]);

  useEffect(() => { }, [key]);

  return <WidgetModulesContext.Provider value={{ widgets, updateWidgets: handleUpdate }}>{children}</WidgetModulesContext.Provider>;
};

export const WidgetModulesConsumer = WidgetModulesContext.Consumer;

export const useWidgetModules = (): WidgetModulesContextValue => useContext(WidgetModulesContext);
